import { useStateContext } from '../../contexts/ContextProvider';

import Button from '../components/Button';
import Text from '../components/Text';
import { useDeconnexion } from '../api/useDeconnexion';
import { t } from '../i18n/langue';
import SelecteurEtablissement from '../shell/SelecteurEtablissement';

/**
 * Zéro écran éligible — décision #17 §4.
 *
 * Le web se replierait sur `/settings`, « qui devrait toujours être accessible ».
 * Ce repli n'est pas transposable : les réglages ne sont pas portés au mobile, ce
 * serait une impasse. D'où un écran assumé plutôt qu'une redirection qui ment.
 *
 * Trois choses le composent, et chacune répare un cul-de-sac :
 *
 * - **`app.trigger-flow.com` en clair**, jamais un lien qui redirige : #14 interdit
 *   toute redirection forcée entre les deux origines. On dit où aller, on n'y
 *   emmène pas.
 * - **La déconnexion**, sans quoi l'appareil reste attaché à un compte qui ne peut
 *   rien faire.
 * - **Le sélecteur d'établissement dès qu'il y en a plusieurs.** L'état « zéro »
 *   est *par établissement*, pas par compte — l'axe module se lit sur
 *   l'établissement sélectionné. Sans lui, un utilisateur multi-établissements est
 *   enfermé alors que le geste qui l'en sort tient en un tap. C'est la version
 *   mobile du piège `/settings` : un repli qui suppose qu'une porte est ouverte.
 *
 * Le message reste **générique** : on ne nomme jamais le module ni la permission
 * manquante (#17 §4).
 */
export default function FinDeCourse() {
    const { currentFacility } = useStateContext();
    const { seDeconnecter, enCours: deconnexionEnCours } = useDeconnexion();

    return (
        <div className="tf-shell h-full overflow-y-auto bg-main-bg-color">
            <main className="mx-auto flex max-w-md flex-col gap-6 px-5 py-8">
                <header className="flex flex-col gap-1">
                    <Text variante="titre" as="h1">
                        {currentFacility?.name ?? 'TriggerFlow'}
                    </Text>
                    <Text variante="petit" ton="attenue">
                        {t('coque.finDeCourse.rienDisponible')}
                    </Text>
                </header>

                <section
                    aria-label={t('coque.finDeCourse.queFaire')}
                    className="rounded-xl border border-border-color-light bg-white p-5"
                >
                    <Text variante="petit" ton="attenue" className="leading-relaxed">
                        {t('coque.finDeCourse.surOrdinateur')}
                    </Text>
                    <Text variante="corps" className="mt-2 font-semibold">
                        app.trigger-flow.com
                    </Text>
                </section>

                <div className="flex flex-col gap-2">
                    <SelecteurEtablissement />
                    <Button
                        libelle={deconnexionEnCours ? t('coque.deconnexionEnCours') : t('coque.seDeconnecter')}
                        variante="secondaire"
                        disabled={deconnexionEnCours}
                        onPress={() => void seDeconnecter()}
                        pleineLargeur
                    />
                </div>
            </main>
        </div>
    );
}
